import { DateTime } from "luxon";
import { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";

export type ResourceBookDaySegment = {
  /** Segment start as millis for sorting / merging */
  startMs: number;
  /** Segment end as millis (exclusive of last instant is not needed; end > start) */
  endMs: number;
  /** True when the resource is free in this interval */
  available: boolean;
};

type Interval = { start: DateTime; end: DateTime };

function clipIntervalToDay(
  start: DateTime,
  end: DateTime,
  dayStart: DateTime,
  dayEndExclusive: DateTime
): Interval | null {
  const s = start < dayStart ? dayStart : start;
  const e = end > dayEndExclusive ? dayEndExclusive : end;
  if (e <= s) {
    return null;
  }
  return { start: s, end: e };
}

function mergeIntervals(intervals: Interval[]): Interval[] {
  if (intervals.length === 0) return [];
  const sorted = [...intervals].sort((a, b) => a.start.valueOf() - b.start.valueOf());
  const merged: Interval[] = [];
  let cur = sorted[0];
  for (let i = 1; i < sorted.length; i += 1) {
    const next = sorted[i];
    if (next.start <= cur.end) {
      cur = { start: cur.start, end: DateTime.max(cur.end, next.end) };
    } else {
      merged.push(cur);
      cur = next;
    }
  }
  merged.push(cur);
  return merged;
}

/**
 * Lists each calendar day from `fromIso` through `toIso` inclusive (yyyy-MM-dd).
 */
export function listIsoDaysInclusive(fromIso: string, toIso: string): string[] {
  const start = DateTime.fromISO(fromIso);
  const end = DateTime.fromISO(toIso);
  if (!start.isValid || !end.isValid || end < start) {
    return [];
  }
  const days: string[] = [];
  for (let d = start; d <= end; d = d.plus({ days: 1 })) {
    const iso = d.toISODate();
    if (iso) days.push(iso);
  }
  return days;
}

/**
 * Removes zero-length segments. When nothing usable remains on a calendar day with
 * positive length, returns a single full-day free segment.
 */
export function finalizeResourceBookDaySegments(
  segments: ResourceBookDaySegment[],
  dayStart: DateTime,
  dayEndExclusive: DateTime
): ResourceBookDaySegment[] {
  const filtered = segments.filter(seg => seg.endMs > seg.startMs);
  const dayLenMs = dayEndExclusive.valueOf() - dayStart.valueOf();
  if (filtered.length === 0 && dayLenMs > 0) {
    return [
      {
        startMs: dayStart.valueOf(),
        endMs: dayEndExclusive.valueOf(),
        available: true
      }
    ];
  }
  return filtered;
}

/**
 * Builds merged booked intervals and free gaps for one calendar day in `timezone`,
 * using schedule `startDate` / `endDate` ISO strings (interpreted then placed in zone).
 */
export function buildResourceBookDayAvailabilitySegments(
  dayIso: string,
  schedules: BookingScheduleInterface[],
  timezone: string
): ResourceBookDaySegment[] {
  const dayStart = DateTime.fromISO(dayIso, { zone: timezone }).startOf("day");
  const dayEndExclusive = dayStart.plus({ days: 1 });

  const bookedRaw: Interval[] = [];
  for (const s of schedules) {
    const start = DateTime.fromISO(s.startDate).setZone(timezone);
    const end = DateTime.fromISO(s.endDate).setZone(timezone);
    if (!start.isValid || !end.isValid) continue;
    const clipped = clipIntervalToDay(start, end, dayStart, dayEndExclusive);
    if (clipped) bookedRaw.push(clipped);
  }

  const booked = mergeIntervals(bookedRaw);
  const segments: ResourceBookDaySegment[] = [];

  let cursor = dayStart;
  for (const b of booked) {
    if (b.start > cursor) {
      segments.push({
        startMs: cursor.valueOf(),
        endMs: b.start.valueOf(),
        available: true
      });
    }
    segments.push({
      startMs: b.start.valueOf(),
      endMs: b.end.valueOf(),
      available: false
    });
    cursor = DateTime.max(b.end, cursor);
  }
  if (cursor < dayEndExclusive) {
    segments.push({
      startMs: cursor.valueOf(),
      endMs: dayEndExclusive.valueOf(),
      available: true
    });
  }

  return finalizeResourceBookDaySegments(segments, dayStart, dayEndExclusive);
}

export function segmentDurationMinutes(seg: ResourceBookDaySegment): number {
  return (seg.endMs - seg.startMs) / 60_000;
}

/** Full calendar day length in minutes (for relative bar width). */
export function fullDayMinutes(): number {
  return 24 * 60;
}
